import { TFunction } from 'i18next';
import { FieldItem } from '@lib/interfaces/form';
import Input from '@lib/components/Input/Input';
import FileUploader from '@lib/components/FilesUploader/FileUploader';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import CheckBoxSingleBool from '@lib/components/CheckBoxSingleBool/CheckBoxSingleBool';
import { FilesPreview } from '@lib/components/FilesUploader/enums';
import {
  getAttachmentValidation,
  getFieldValidation,
} from '@lib/utils/validationUtils';
import {
  FileSizeLimits,
  UppyDocumentationImportAllowedFileTypes,
  videoSizeLimit,
} from '@lib/enums/mimeTypes';
import { ButtonSizes } from '@lib/components/Button/Button';
import { MAX_TEXT_EDITOR_FIELD_LENGTH } from '@lib/enums/form';

export enum VersionFormFieldNames {
  DocumentName = 'documentName',
  Attachment = 'attachmentAttached',
  Notes = 'notes',
  RequireReread = 'requireReread',
}

export default function getVersionFormFields({
  isFullAdmin = true,
  isPlanLocked,
  t,
}: {
  isFullAdmin?: boolean;
  isPlanLocked?: boolean;
  onUpgrade?: () => void;
  t: TFunction<'translation', undefined>;
}): FieldItem[] {
  // The require-reread checkbox is purely a Full-Admin upgrade affordance
  // when locked — hide it entirely for User-role accounts.
  const hideRequireReread = !!isPlanLocked && !isFullAdmin;
  return [
    {
      name: VersionFormFieldNames.DocumentName,
      label: t('document-name'),
      element: Input,
      validation: getFieldValidation({ t }),
      componentProps: {
        disabled: true,
      },
      isOptionalFn: () => false,
    },
    {
      name: VersionFormFieldNames.Attachment,
      label: '',
      validation: getAttachmentValidation({ required: true, t }),
      element: FileUploader,
      componentProps: {
        filesView: FilesPreview.Documents,
        allowedFileTypes: UppyDocumentationImportAllowedFileTypes,
        note: t('documents-import-files-upload-note', {
          sizeImg: FileSizeLimits.Image,
          sizePdf: FileSizeLimits.File,
          sizeVideo: FileSizeLimits.Video,
        }),
        maxFileSize: videoSizeLimit,
        uploadButtonProps: {
          buttonText: t('upload-file'),
          buttonSize: ButtonSizes.large,
          leftIcon: 'upload',
          fullWidth: true,
        },
      },
    },
    {
      name: VersionFormFieldNames.Notes,
      label: t('version-notes'),
      element: TextEditor,
      validation: getFieldValidation({
        required: true,
        maxLength: MAX_TEXT_EDITOR_FIELD_LENGTH,
        t,
      }),
    },
    ...(hideRequireReread
      ? []
      : [
          {
            name: VersionFormFieldNames.RequireReread,
            label: t('require-reread'),
            element: CheckBoxSingleBool,
            componentProps: {
              options: [
                {
                  value: 'true',
                },
              ],
              disabled: isPlanLocked,
              lockedProps: {
                locked: isPlanLocked,
                tooltip: t('upgrade-to-unlock-this-feature'),
              },
            },
          } as FieldItem,
        ]),
  ];
}
